
import React from "react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export interface LoadingSpinnerProps {
  /** Custom message to display (defaults to "Loading...") */
  message?: string;
  /** Size variant of the spinner */
  size?: "xs" | "sm" | "md" | "lg";
  /** Visual style variant */
  variant?: "primary" | "secondary" | "minimal" | "glassmorphic";
  /** Center the spinner in its container */
  centered?: boolean;
  /** Add a pulsing effect to the spinner */
  pulse?: boolean;
  /** Additional class names */
  className?: string;
}

export function LoadingSpinner({
  message = "Loading...",
  size = "md",
  variant = "primary",
  centered = false,
  pulse = false,
  className,
}: LoadingSpinnerProps) {
  const { isMobile, screenSize } = useIsMobile();
  
  // Adjust sizes based on mobile view
  const adjustedSize = isMobile && screenSize === 'xs' && size === 'md' ? 'sm' : size;

  // Size mappings
  const sizeClasses = {
    xs: "h-3 w-3",
    sm: "h-4 w-4",
    md: "h-6 w-6",
    lg: "h-10 w-10",
  };

  // Variant mappings
  const variantClasses = {
    primary: "text-primary",
    secondary: "text-secondary",
    minimal: "text-muted-foreground",
    glassmorphic: "text-white",
  };

  // Content container classes based on size
  const contentClasses = {
    xs: "text-xs gap-1.5",
    sm: "text-xs gap-2",
    md: "text-sm gap-3",
    lg: "text-base gap-4",
  };

  // Container classes
  const containerClasses = cn(
    "flex items-center",
    contentClasses[adjustedSize],
    centered && "justify-center",
    variant === 'glassmorphic' && "p-3 rounded-lg backdrop-blur-md bg-primary/10 border border-white/20 shadow-md",
    className
  );

  // Animation classes
  const animationClasses = cn(
    "animate-spin",
    pulse && "animate-pulse",
    sizeClasses[adjustedSize],
    variantClasses[variant]
  );

  return (
    <div className={containerClasses}>
      <Loader2 className={animationClasses} />
      {message && (
        <span className={cn(
          "animate-fadeIn",
          variant === 'glassmorphic' ? "text-white" : "text-muted-foreground"
        )}>
          {message}
        </span>
      )}
    </div>
  );
}
